import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

interface LocationState {
  pathname: string;
  search: string;
}

interface RouterValue extends LocationState {
  navigate: (to: string, replace?: boolean) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export const normalizePathname = (pathname: string) => {
  const value = pathname || "/";
  return value === "/" ? value : value.replace(/\/+$/, "") || "/";
};

const stripBasePath = (pathname: string) => {
  if (!basePath) return normalizePathname(pathname);
  if (pathname === basePath || pathname === `${basePath}/`) return "/";
  const stripped = pathname.startsWith(`${basePath}/`)
    ? pathname.slice(basePath.length)
    : pathname;
  return normalizePathname(stripped);
};

const internalHref = (to: string) => {
  if (/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(to) || to.startsWith("#")) return to;
  const url = new URL(to, "https://router.local");
  return `${basePath}${url.pathname}${url.search}${url.hash}` || "/";
};

const currentLocation = (): LocationState => ({
  pathname: stripBasePath(window.location.pathname),
  search: window.location.search,
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<LocationState>(currentLocation);

  useEffect(() => {
    const onPopState = () => setLocation(currentLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to: string, replace = false) => {
    const url = new URL(internalHref(to), window.location.origin);
    if (url.origin !== window.location.origin) {
      window.location.assign(url.href);
      return;
    }
    const target = `${url.pathname}${url.search}${url.hash}`;
    if (replace) window.history.replaceState({}, "", target);
    else window.history.pushState({}, "", target);
    setLocation(currentLocation());
  }, []);

  const value = useMemo(() => ({ ...location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

const useRouter = () => {
  const value = useContext(RouterContext);
  if (!value) throw new Error("RouterProvider is missing.");
  return value;
};

export const useLocation = () => {
  const { pathname, search } = useRouter();
  return { pathname, search };
};

export const useNavigate = () => useRouter().navigate;

export const useSearchParams = () => {
  const { pathname, search, navigate } = useRouter();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const setParams = useCallback(
    (next: URLSearchParams | Record<string, string>) => {
      const value = next instanceof URLSearchParams ? next : new URLSearchParams(next);
      const query = value.toString();
      navigate(`${pathname}${query ? `?${query}` : ""}`, true);
    },
    [navigate, pathname],
  );
  return [params, setParams] as const;
};

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
}

export function Link({ to, onClick, children, ...props }: LinkProps) {
  const navigate = useNavigate();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      props.target === "_blank"
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };
  return <a href={internalHref(to)} onClick={handleClick} {...props}>{children}</a>;
}

interface NavLinkProps extends Omit<LinkProps, "className"> {
  className?: string | ((state: { isActive: boolean }) => string);
}

export function NavLink({ to, className, ...props }: NavLinkProps) {
  const { pathname } = useLocation();
  const targetPath = new URL(to, window.location.origin).pathname;
  const isActive = pathname === targetPath;
  const resolvedClass = typeof className === "function" ? className({ isActive }) : className;
  return <Link to={to} className={resolvedClass} aria-current={isActive ? "page" : undefined} {...props} />;
}
